import chroma from 'chroma-js';
import * as d3 from 'd3';
import colors from 'tailwindcss/colors';

import {Translator} from 'src/components/TranslationsContext';
import {
  VizConfig,
  VizConfigCreatorItem,
  VizConfigStyle,
  findCIBands,
  hfGetStyle,
  makeConfigItems,
  makeHFCIBandConfigs,
} from 'src/components/Viz/config';
import {H5ObservationVariant} from 'src/lib/api/h5';
import {FlowForecastColumns, Source, TimeAggregation} from 'src/lib/api/series';
import COLORS from 'src/lib/colors';
import {H5_OBSERVATION_COLUMN_BY_VARIANT} from 'src/lib/h5/toObservationTimeSeries';

// One per model drawn on the chart. The first keeps the legacy HydroForecast colors; the rest
// are picked to stay apart from those and from the observation gray.
const EXTRA_MODEL_COLORS = [
  colors.amber[600],
  colors.fuchsia[700],
  colors.lime[700],
  colors.rose[600],
  colors.violet[600],
  colors.emerald[700],
];

export type H5FlowModel = {
  // The chart matches series to config items by source, so each model drawn needs its own.
  source: Source;
  name: string;
  columns: FlowForecastColumns[];
  // 0 for the default model; the others count up through the models drawn, so no two share a
  // color until there are more than the palette holds.
  colorIndex: number;
  // A single-valued forecast, such as an RFC's: its one line is the model's forecast, not a mean.
  deterministic?: boolean;
};

// The legacy charts' 50% and 90% bands. A model without one, like TiRex, shows its band closest
// to it instead, the wider on a tie.
const DEFAULT_CI_PCTS = [50, 90];

function shownCIPcts(columns: FlowForecastColumns[]): number[] {
  const available = findCIBands(columns).map((band) => band.ciPct);
  const closestTo = (target: number) =>
    available.reduce<number | undefined>(
      (best, ciPct) =>
        best === undefined ||
        Math.abs(ciPct - target) < Math.abs(best - target) ||
        (Math.abs(ciPct - target) === Math.abs(best - target) && ciPct > best)
          ? ciPct
          : best,
      undefined
    );
  return [...new Set(DEFAULT_CI_PCTS.flatMap((target) => closestTo(target) ?? []))];
}

function modelConfigItems(
  {source, name, columns, colorIndex, deterministic}: H5FlowModel,
  curveFactory: d3.CurveFactory,
  t: Translator
): {bands: VizConfigCreatorItem[]; lines: VizConfigCreatorItem[]} {
  const hasMean = columns.includes(FlowForecastColumns.DISCHARGE_MEAN);
  const baseColor =
    colorIndex === 0 ? undefined : EXTRA_MODEL_COLORS[(colorIndex - 1) % EXTRA_MODEL_COLORS.length];
  const getLineStyle = baseColor
    ? (idx = 0, length = 0): VizConfigStyle => ({
        stroke:
          length > 1
            ? chroma.scale([chroma.mix(baseColor, '#fff', 0.6), baseColor]).colors(length)[idx]
            : baseColor,
        strokeWidth: 2,
      })
    : hfGetStyle;

  const bands = makeHFCIBandConfigs({
    source,
    availableColumns: columns,
    curveFactory,
    displayNamePrefix: name,
    loColor: baseColor ?? COLORS.HF_50,
    hiColor: baseColor ? chroma.mix(baseColor, '#fff', 0.5).hex() : COLORS.HF_90,
    shownCIPcts: shownCIPcts(columns),
    legendGroup: name,
  });

  const lines: VizConfigCreatorItem[] = [];
  if (columns.includes(FlowForecastColumns.DISCHARGE_MEDIAN)) {
    lines.push({
      displayName: `${name} ${t('median')}`,
      source,
      columns: [FlowForecastColumns.DISCHARGE_MEDIAN],
      getStyle: getLineStyle,
      legendIcon: 'line',
      isHidden: hasMean,
      showForecastTimeMarkers: !hasMean,
      curveFactory,
      legendOptions: {shortDisplayName: t('median'), group: name},
    });
  }
  if (hasMean) {
    lines.push({
      displayName: deterministic ? name : `${name} ${t('mean')}`,
      source,
      columns: [FlowForecastColumns.DISCHARGE_MEAN],
      getStyle: getLineStyle,
      legendIcon: 'line',
      showForecastTimeMarkers: true,
      curveFactory,
      legendOptions: {shortDisplayName: deterministic ? undefined : t('mean'), group: name},
    });
  }
  return {bands, lines};
}

/**
 * The chart config for H5 flow forecasts, built from the columns each model publishes. Bands,
 * lines and colors come from the same builders as the legacy flow charts, so an H5 section looks
 * like the legacy one it replaces.
 */
export function makeH5FlowVizConfig({
  models,
  observationVariants = [],
  timeAggregation,
  t,
}: {
  models: H5FlowModel[];
  observationVariants?: H5ObservationVariant[];
  timeAggregation: TimeAggregation;
  t: Translator;
}): VizConfig {
  const curveFactory = timeAggregation === '1H' ? d3.curveLinear : d3.curveStepAfter;
  const itemsByModel = models.map((model) => modelConfigItems(model, curveFactory, t));

  // As on the legacy flow chart, a cleaned series dims the raw one and starts it hidden.
  const hasCleaned = observationVariants.includes('cleaned');
  const observations = (['raw', 'cleaned'] as const)
    .filter((variant) => observationVariants.includes(variant))
    .map((variant): VizConfigCreatorItem => {
      const dimmed = variant === 'raw' && hasCleaned;
      return {
        displayName: t(variant === 'raw' ? 'observed' : 'observedClean'),
        source: Source.HFObservations,
        columns: [H5_OBSERVATION_COLUMN_BY_VARIANT[variant]],
        getStyle: () => ({
          hfStrokeOutlineStyle: {stroke: 'white', strokeWidth: 3},
          stroke: COLORS.OBS,
          strokeOpacity: dimmed ? 0.4 : 1,
          strokeWidth: 2,
        }),
        legendIcon: 'line',
        curveFactory,
        defaultNotVisible: dimmed,
        // Grouped so the observations keep a column when the legend is expanded, which lists
        // only grouped items.
        legendOptions: {group: t('observed')},
      };
    });

  // Every model's bands first so they paint behind every line.
  return {
    forecasts: makeConfigItems('forecasts', [
      ...itemsByModel.flatMap(({bands}) => bands),
      ...itemsByModel.flatMap(({lines}) => lines),
    ]),
    observations: makeConfigItems('observations', observations),
  };
}
